let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Dimensions,
  ScrollView,
  TextInput,
  Modal,
  Alert,
  BackHandler,
  ActivityIndicator,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import { COMMERCE_DISABLED } from '../../utils/iosCommerce';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

// ✅ Safe string normalizer
const s = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v));

// JOBENG Logo component
const JobengLogo = () => (
  <Image
    source={require('../../../assets/jobeng_icon.png')}
    style={{ width: 30, height: 30 }}
    resizeMode="contain"
  />
);

const WelcomeProfessionalScreen = ({ navigation }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // State for subscription status
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // State for payment modal
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // State for locked feature modal
  const [lockedModalVisible, setLockedModalVisible] = useState(false);
  const [lockedFeature, setLockedFeature] = useState('');

  // ---------------------------
  // Helpers
  // ---------------------------
  const safeJson = async (response) => {
    const text = await response.text();
    try {
      return text ? JSON.parse(text) : null;
    } catch {
      return { message: text };
    }
  };

  const getToken = async () => {
    const t1 = await AsyncStorage.getItem('auth_token');
    const t2 = await AsyncStorage.getItem(`professional_${_profUserId}_token`);
    return t1 || t2 || '';
  };

  const getUser = async () => {
    const u1 = await AsyncStorage.getItem(`professional_${_profUserId}_user_data`);
    const u2 = await AsyncStorage.getItem(`professional_${_profUserId}_user`);
    const raw = u1 || u2 || '';
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return { raw };
    }
  };

  const saveSubscriptionStatusLocal = async (active) => {
    try {
      if (active) {
        await AsyncStorage.setItem(`professional_${_profUserId}_jobeng_professional_subscription`, 'active');
      } else {
        await AsyncStorage.removeItem(`professional_${_profUserId}_jobeng_professional_subscription`);
      }
      setIsSubscribed(!!active);
    } catch (error) {
      console.error('Error saving subscription status:', error);
    }
  };

  // ---------------------------
  // DB Subscription Status (safe multi-endpoint)
  // ---------------------------
  const fetchSubscriptionStatusFromDB = async (token) => {
    if (!token) return null;

    const endpoints = [
      '/api/subscription/status',
      '/api/subscriptions/status',
      '/api/billing/subscription/status',
      '/api/professional/subscription/status',
    ];

    for (const ep of endpoints) {
      try {
        const res = await fetch(`${API_BASE_URL}${ep}`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await safeJson(res);

        if (res.ok) {
          // Accept many shapes:
          // {active:true} | {success:true, data:{active:true}} | {status:"active"} | {subscription:{status:"active"}}
          const active =
            data?.active === true ||
            data?.data?.active === true ||
            s(data?.status).toLowerCase() === 'active' ||
            s(data?.data?.status).toLowerCase() === 'active' ||
            s(data?.subscription?.status).toLowerCase() === 'active' ||
            s(data?.data?.subscription?.status).toLowerCase() === 'active';

          return { active, raw: data };
        }
      } catch {
        // try next endpoint
      }
    }

    return null; // not available / not reachable
  };

  // ---------------------------
  // DB Subscribe (safe multi-endpoint)
  // NOTE: You can replace with your Stripe Checkout flow later.
  // ---------------------------
  const createSubscriptionInDB = async (token) => {
    if (!token) return false;

    const endpoints = [
      '/api/subscription/subscribe',
      '/api/subscriptions/subscribe',
      '/api/billing/subscribe',
      '/api/professional/subscription/subscribe',
    ];

    for (const ep of endpoints) {
      try {
        const res = await fetch(`${API_BASE_URL}${ep}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            plan: 'professional_premium_monthly',
            price_gbp: 14,
            currency: 'GBP',
            source: 'app_welcome_screen',
          }),
        });
        const data = await safeJson(res);
        if (res.ok && (data?.success === true || s(data?.status).toLowerCase() === 'ok')) {
          return true;
        }
      } catch {
        // try next endpoint
      }
    }

    return false;
  };

  // ---------------------------
  // Initial Subscription Check (local + DB)
  // ---------------------------
  const checkSubscriptionStatus = async () => {
    setIsChecking(true);
    try {
      const token = await getToken();
      const user = await getUser();

      // If user is not logged in, send them to login
      if (!token) {
        setIsChecking(false);
        navigation.navigate('ProfessionalLogin');
        return;
      }

      // 1) Local quick check
      const local = await AsyncStorage.getItem(`professional_${_profUserId}_jobeng_professional_subscription`);
      if (local === 'active') {
        setIsSubscribed(true);
      }

      // 2) DB check (authoritative)
      const db = await fetchSubscriptionStatusFromDB(token);
      if (db && typeof db.active === 'boolean') {
        await saveSubscriptionStatusLocal(db.active);
      } else {
        // keep local result if DB endpoint not available
        if (local === 'active') setIsSubscribed(true);
      }

      // Optional: ensure user type is professional in storage
      if (user && user.user_type !== 'professional') {
        user.user_type = 'professional';
        user.profile_type = user.profile_type || 'professional';
        await AsyncStorage.setItem(`professional_${_profUserId}_user_data`, JSON.stringify(user));
        await AsyncStorage.setItem(`professional_${_profUserId}_user`, JSON.stringify(user));
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    } finally {
      setIsChecking(false);
    }
  };

  // Check subscription status on mount
  useEffect(() => {
    checkSubscriptionStatus();

    // Handle hardware back button
    const backHandler = BackHandler.addEventListener('hardwareBackPress', handleBackPress);
    return () => backHandler.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle back button press
  const handleBackPress = () => {
    if (paymentModalVisible) {
      setPaymentModalVisible(false);
      return true;
    }
    if (lockedModalVisible) {
      setLockedModalVisible(false);
      return true;
    }
    return false;
  };

  // Professional features to showcase
  const professionalFeatures = [
    {
      id: 'feature1',
      title: 'Premium Job Access',
      description: 'Access to exclusive high-paying engineering positions',
      icon: 'briefcase-variant',
    },
    {
      id: 'feature2',
      title: 'AI Career Matching',
      description: 'Advanced AI algorithms match you with perfect opportunities',
      icon: 'brain',
    },
    {
      id: 'feature3',
      title: 'Skills Development',
      description: 'Access to premium courses and certification programs',
      icon: 'school',
    },
    {
      id: 'feature4',
      title: 'Career Coaching',
      description: 'One-on-one sessions with industry experts',
      icon: 'account-supervisor',
    },
  ];

  // Subscription benefits
  const subscriptionBenefits = [
    'Priority application to premium jobs',
    'Early access to new internship postings',
    'AI-powered job matching',
    'Professional profile highlighting',
    'Direct messaging with employers',
  ];

  // Format card number with spaces
  const formatCardNumber = (text) => {
    const cleaned = s(text).replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const chunks = [];
    for (let i = 0; i < cleaned.length; i += 4) chunks.push(cleaned.substr(i, 4));
    return chunks.join(' ').substr(0, 19); // 16 digits + 3 spaces
  };

  // Format expiry date with slash
  const formatExpiryDate = (text) => {
    const cleaned = s(text).replace(/[^0-9]/gi, '');
    if (cleaned.length >= 3) return `${cleaned.substr(0, 2)}/${cleaned.substr(2, 2)}`;
    if (cleaned.length === 2) return `${cleaned}/`;
    return cleaned;
  };

  // Handle payment submission with DB + navigation
  const handlePaymentSubmit = async () => {
    // Basic validation
    if (cardNumber.replace(/\s+/g, '').length !== 16) {
      Alert.alert('Invalid Card', 'Please enter a valid 16-digit card number');
      return;
    }
    if (s(cardName).trim().length < 3) {
      Alert.alert('Invalid Name', 'Please enter the cardholder name');
      return;
    }
    if (s(expiryDate).length !== 5) {
      Alert.alert('Invalid Date', 'Please enter a valid expiry date (MM/YY)');
      return;
    }
    if (s(cvv).length < 3) {
      Alert.alert('Invalid CVV', 'Please enter a valid security code');
      return;
    }

    setIsProcessing(true);

    try {
      // ✅ Try to create subscription in DB (if endpoint exists)
      const token = await getToken();
      if (!token) {
        setIsProcessing(false);
        Alert.alert('Session Expired', 'Please login again.');
        navigation.navigate('ProfessionalLogin');
        return;
      }

      // Simulate processing UI while we do DB call
      const dbOk = await createSubscriptionInDB(token);

      // Show success UI regardless (keeps your UX),
      // but if DB fails, we still enable locally and user can proceed.
      setIsProcessing(false);
      setPaymentSuccess(true);

      setTimeout(async () => {
        await saveSubscriptionStatusLocal(true);
        setPaymentModalVisible(false);
        setPaymentSuccess(false);

        // Navigate to professional dashboard
        navigation.navigate('ProfessionalDashboard');
      }, 1500);
    } catch (e) {
      console.error('Payment/subscribe error:', e);
      setIsProcessing(false);
      Alert.alert('Payment Error', 'Failed to process subscription. Please try again.');
    }
  };

  // Handle locked feature attempt
  const handleLockedFeature = (featureName) => {
    setLockedFeature(featureName);
    setLockedModalVisible(true);
  };

  // Handle navigation based on subscription status
  const handleNavigation = (screenName) => {
    if (isSubscribed) {
      navigation.navigate(screenName);
    } else {
      handleLockedFeature(screenName);
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* Header with JOBENG Logo */}
      <View style={styles.header}>
        <JobengLogo />
        <Text style={styles.headerTitle}>JOBENG Professional</Text>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* ✅ Checking banner */}
        {isChecking && (
          <View style={{ paddingVertical: 14, alignItems: 'center' }}>
            <ActivityIndicator size={20} color="#0e4e96" />
            <Text style={{ marginTop: 8, color: '#64748b' }}>Checking subscription…</Text>
          </View>
        )}

        {/* Welcome Section */}
        <View style={styles.welcomeSection}>
          <View style={styles.welcomeTextContainer}>
            <Text style={styles.welcomeTitle}>Welcome to</Text>
            <Text style={styles.jobengTitle}>JOBENG Professional</Text>
            <Text style={styles.welcomeDescription}>
              Your gateway to premium engineering opportunities and career advancement
            </Text>
          </View>

          <View style={styles.illustrationContainer}>
            <MaterialCommunityIcons name="account-tie" size={120} color="#4285F4" />
          </View>
        </View>

        {/* Subscription Section */}
        <View style={styles.subscriptionSection}>
          <View style={styles.subscriptionHeader}>
            <MaterialCommunityIcons name="star" size={28} color="#FFD700" />
            <Text style={styles.subscriptionTitle}>Professional Premium</Text>
          </View>

          {!COMMERCE_DISABLED && (
            <View style={styles.pricingContainer}>
              <Text style={styles.pricingAmount}>£14</Text>
              <Text style={styles.pricingPeriod}>per month</Text>
            </View>
          )}

          <View style={styles.benefitsContainer}>
            {subscriptionBenefits.map((benefit, index) => (
              <View key={index} style={styles.benefitItem}>
                <MaterialCommunityIcons name="check-circle" size={20} color="#16a34a" />
                <Text style={styles.benefitText}>{benefit}</Text>
              </View>
            ))}
          </View>

          {isSubscribed ? (
            <View style={styles.activeSubscriptionContainer}>
              <MaterialCommunityIcons name="check-decagram" size={24} color="#16a34a" />
              <Text style={styles.activeSubscriptionText}>Subscription Active</Text>
            </View>
          ) : COMMERCE_DISABLED ? (
            <View style={styles.lockNoticeContainer}>
              <MaterialCommunityIcons name="information-outline" size={18} color="#64748b" />
              <Text style={styles.lockNoticeText}>Premium isn’t available for purchase in the app. Everything included with your current plan is ready to use.</Text>
            </View>
          ) : (
            <>
              <TouchableOpacity style={styles.subscribeButton} onPress={() => setPaymentModalVisible(true)}>
                <MaterialCommunityIcons
                  name="credit-card-outline"
                  size={20}
                  color="#fff"
                  style={styles.buttonIcon}
                />
                <Text style={styles.subscribeButtonText}>Subscribe Now</Text>
              </TouchableOpacity>

              <Text style={styles.subscriptionNote}>Cancel anytime. No long-term commitment required.</Text>
            </>
          )}

          {!isSubscribed && (
            <View style={styles.lockNoticeContainer}>
              <MaterialCommunityIcons name="lock" size={18} color="#dc2626" />
              <Text style={styles.lockNoticeText}>Premium features are locked until subscription is active</Text>
            </View>
          )}
        </View>

        {/* Features Section */}
        <View style={[styles.featuresSection, !isSubscribed && styles.lockedSection]}>
          <Text style={styles.sectionTitle}>Premium Features</Text>

          {professionalFeatures.map((feature) => (
            <View key={feature.id} style={styles.featureCard}>
              <View style={styles.featureIconContainer}>
                <MaterialCommunityIcons name={feature.icon} size={28} color="#4285F4" />
              </View>
              <View style={styles.featureContent}>
                <Text style={styles.featureTitle}>{feature.title}</Text>
                <Text style={styles.featureDescription}>{feature.description}</Text>
              </View>
              {!isSubscribed && (
                <MaterialCommunityIcons
                  name="lock"
                  size={20}
                  color="#64748b"
                  style={styles.featureLockIcon}
                />
              )}
            </View>
          ))}
        </View>

        {/* Call to Action */}
        <View style={styles.ctaSection}>
          <TouchableOpacity
            style={[styles.primaryButton, !isSubscribed && styles.lockedButton]}
            onPress={() => handleNavigation('ProfessionalDashboard')}
          >
            <Text style={styles.primaryButtonText}>Go to Dashboard</Text>
            {isSubscribed ? (
              <Ionicons name="arrow-forward" size={20} color="#fff" />
            ) : (
              <MaterialCommunityIcons name="lock" size={20} color="#fff" />
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.secondaryButton, !isSubscribed && styles.lockedSecondaryButton]}
            onPress={() => handleNavigation('FindJobs')}
          >
            <Text style={[styles.secondaryButtonText, !isSubscribed && styles.lockedSecondaryButtonText]}>
              Browse Premium Jobs
            </Text>
            {!isSubscribed && (
              <MaterialCommunityIcons name="lock" size={16} color="#94a3b8" style={{ marginLeft: 8 }} />
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Payment Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={paymentModalVisible && !COMMERCE_DISABLED}
        onRequestClose={() => setPaymentModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Payment Details</Text>
              <TouchableOpacity style={styles.closeButton} onPress={() => setPaymentModalVisible(false)}>
                <Ionicons name="close" size={24} color="#64748b" />
              </TouchableOpacity>
            </View>

            {!isProcessing && !paymentSuccess ? (
              <ScrollView style={styles.modalContent}>
                <View style={styles.subscriptionSummary}>
                  <Text style={styles.summaryTitle}>Professional Premium</Text>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Monthly subscription</Text>
                    <Text style={styles.summaryValue}>£14.00</Text>
                  </View>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>VAT (20%)</Text>
                    <Text style={styles.summaryValue}>£2.80</Text>
                  </View>
                  <View style={[styles.summaryRow, styles.totalRow]}>
                    <Text style={styles.totalLabel}>Total</Text>
                    <Text style={styles.totalValue}>£16.80</Text>
                  </View>
                </View>

                <View style={styles.paymentForm}>
                  <Text style={styles.inputLabel}>Card Number</Text>
                  <View style={styles.cardNumberContainer}>
                    <TextInput
                      style={styles.cardNumberInput}
                      placeholder="1234 5678 9012 3456"
                      keyboardType="number-pad"
                      maxLength={19}
                      value={cardNumber}
                      onChangeText={(text) => setCardNumber(formatCardNumber(text))}
                    />
                    <View style={styles.cardTypeContainer}>
                      <FontAwesome5 name="cc-visa" size={24} color="#1a1f71" />
                      <FontAwesome5 name="cc-mastercard" size={24} color="#eb001b" style={{ marginLeft: 8 }} />
                    </View>
                  </View>

                  <Text style={styles.inputLabel}>Cardholder Name</Text>
                  <TextInput style={styles.input} placeholder="John Smith" value={cardName} onChangeText={setCardName} />

                  <View style={styles.rowInputs}>
                    <View style={styles.halfInput}>
                      <Text style={styles.inputLabel}>Expiry Date</Text>
                      <TextInput
                        style={styles.input}
                        placeholder="MM/YY"
                        keyboardType="number-pad"
                        maxLength={5}
                        value={expiryDate}
                        onChangeText={(text) => setExpiryDate(formatExpiryDate(text))}
                      />
                    </View>
                    <View style={styles.halfInput}>
                      <Text style={styles.inputLabel}>CVV</Text>
                      <TextInput
                        style={styles.input}
                        placeholder="123"
                        keyboardType="number-pad"
                        maxLength={4}
                        value={cvv}
                        onChangeText={setCvv}
                        secureTextEntry
                      />
                    </View>
                  </View>

                  <TouchableOpacity style={styles.payButton} onPress={handlePaymentSubmit}>
                    <Text style={styles.payButtonText}>Pay £16.80</Text>
                  </TouchableOpacity>

                  <View style={styles.securePaymentInfo}>
                    <MaterialCommunityIcons name="shield-check" size={20} color="#64748b" />
                    <Text style={styles.securePaymentText}>Secure payment processed by Stripe</Text>
                  </View>
                </View>
              </ScrollView>
            ) : isProcessing ? (
              <View style={styles.processingContainer}>
                <MaterialCommunityIcons name="credit-card-scan" size={60} color="#4285F4" />
                <Text style={styles.processingText}>Processing payment...</Text>
              </View>
            ) : (
              <View style={styles.successContainer}>
                <MaterialCommunityIcons name="check-circle" size={60} color="#16a34a" />
                <Text style={styles.successTitle}>Payment Successful!</Text>
                <Text style={styles.successText}>
                  Welcome to Professional Premium! You now have access to all features.
                </Text>
              </View>
            )}
          </View>
        </View>
      </Modal>

      {/* Locked Feature Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={lockedModalVisible}
        onRequestClose={() => setLockedModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.lockedModalContainer}>
            <MaterialCommunityIcons name="lock" size={48} color="#dc2626" />
            <Text style={styles.lockedModalTitle}>Premium Feature</Text>
            <Text style={styles.lockedModalText}>
              {lockedFeature} is available with Professional Premium subscription.
            </Text>
            <View style={styles.lockedModalButtons}>
              <TouchableOpacity style={styles.cancelButton} onPress={() => setLockedModalVisible(false)}>
                <Text style={styles.cancelButtonText}>Close</Text>
              </TouchableOpacity>
              {!COMMERCE_DISABLED && (
                <TouchableOpacity
                  style={styles.upgradeButton}
                  onPress={() => {
                    setLockedModalVisible(false);
                    setPaymentModalVisible(true);
                  }}
                >
                  <Text style={styles.upgradeButtonText}>Subscribe Now</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#f8fafc' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  headerTitle: { fontSize: 18, fontWeight: '600', color: '#1e293b' },
  scrollView: { flex: 1 },

  welcomeSection: { backgroundColor: '#fff', paddingHorizontal: 20, paddingVertical: 30, alignItems: 'center' },
  welcomeTextContainer: { alignItems: 'center', marginBottom: 30 },
  welcomeTitle: { fontSize: 24, color: '#64748b', marginBottom: 5 },
  jobengTitle: { fontSize: 32, fontWeight: 'bold', color: '#0e4e96', marginBottom: 15 },
  welcomeDescription: { fontSize: 16, color: '#64748b', textAlign: 'center', lineHeight: 24, paddingHorizontal: 20 },
  illustrationContainer: { alignItems: 'center', justifyContent: 'center' },

  subscriptionSection: { backgroundColor: '#fff', marginTop: 10, paddingHorizontal: 20, paddingVertical: 25 },
  subscriptionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
  subscriptionTitle: { fontSize: 24, fontWeight: 'bold', color: '#1e293b', marginLeft: 10 },
  pricingContainer: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'center', marginBottom: 25 },
  pricingAmount: { fontSize: 48, fontWeight: 'bold', color: '#0e4e96' },
  pricingPeriod: { fontSize: 18, color: '#64748b', marginLeft: 5 },
  benefitsContainer: { marginBottom: 25 },
  benefitItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  benefitText: { fontSize: 16, color: '#374151', marginLeft: 12, flex: 1 },

  activeSubscriptionContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0fdf4',
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#bbf7d0',
  },
  activeSubscriptionText: { fontSize: 16, fontWeight: '600', color: '#16a34a', marginLeft: 8 },

  subscribeButton: {
    backgroundColor: '#0e4e96',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    marginBottom: 15,
  },
  buttonIcon: { marginRight: 8 },
  subscribeButtonText: { color: '#fff', fontSize: 18, fontWeight: '600' },
  subscriptionNote: { fontSize: 14, color: '#64748b', textAlign: 'center', marginBottom: 20 },

  lockNoticeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fef2f2',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  lockNoticeText: { fontSize: 14, color: '#dc2626', marginLeft: 8, textAlign: 'center' },

  featuresSection: { backgroundColor: '#fff', marginTop: 10, paddingHorizontal: 20, paddingVertical: 25 },
  lockedSection: { opacity: 0.7 },
  sectionTitle: { fontSize: 20, fontWeight: 'bold', color: '#1e293b', marginBottom: 20, textAlign: 'center' },

  featureCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  featureIconContainer: {
    width: 50,
    height: 50,
    backgroundColor: '#e3f2fd',
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 15,
  },
  featureContent: { flex: 1 },
  featureTitle: { fontSize: 16, fontWeight: '600', color: '#1e293b', marginBottom: 4 },
  featureDescription: { fontSize: 14, color: '#64748b', lineHeight: 20 },
  featureLockIcon: { marginLeft: 10 },

  ctaSection: { paddingHorizontal: 20, paddingVertical: 25, paddingBottom: 40 },
  primaryButton: {
    backgroundColor: '#0e4e96',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    marginBottom: 15,
  },
  lockedButton: { backgroundColor: '#94a3b8' },
  primaryButtonText: { color: '#fff', fontSize: 18, fontWeight: '600', marginRight: 8 },

  secondaryButton: {
    backgroundColor: 'transparent',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#0e4e96',
  },
  lockedSecondaryButton: { borderColor: '#94a3b8' },
  secondaryButtonText: { color: '#0e4e96', fontSize: 16, fontWeight: '600' },
  lockedSecondaryButtonText: { color: '#94a3b8' },

  // Modal styles
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)', justifyContent: 'center', alignItems: 'center' },
  modalContainer: { backgroundColor: '#fff', borderRadius: 20, width: width * 0.9, maxHeight: height * 0.8, overflow: 'hidden' },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#1e293b' },
  closeButton: { padding: 4 },
  modalContent: { paddingHorizontal: 20, paddingVertical: 20 },

  subscriptionSummary: { backgroundColor: '#f8fafc', padding: 20, borderRadius: 12, marginBottom: 24 },
  summaryTitle: { fontSize: 18, fontWeight: 'bold', color: '#1e293b', marginBottom: 16 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },
  summaryLabel: { fontSize: 16, color: '#64748b' },
  summaryValue: { fontSize: 16, color: '#1e293b', fontWeight: '500' },
  totalRow: { borderTopWidth: 1, borderTopColor: '#e2e8f0', paddingTop: 12, marginTop: 8 },
  totalLabel: { fontSize: 18, fontWeight: 'bold', color: '#1e293b' },
  totalValue: { fontSize: 18, fontWeight: 'bold', color: '#0e4e96' },

  paymentForm: { marginBottom: 20 },
  inputLabel: { fontSize: 16, fontWeight: '500', color: '#374151', marginBottom: 8 },
  input: { borderWidth: 1, borderColor: '#d1d5db', borderRadius: 8, paddingHorizontal: 16, paddingVertical: 12, fontSize: 16, backgroundColor: '#fff', marginBottom: 16 },
  cardNumberContainer: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#d1d5db', borderRadius: 8, backgroundColor: '#fff', marginBottom: 16 },
  cardNumberInput: { flex: 1, paddingHorizontal: 16, paddingVertical: 12, fontSize: 16 },
  cardTypeContainer: { flexDirection: 'row', paddingHorizontal: 12 },
  rowInputs: { flexDirection: 'row', justifyContent: 'space-between' },
  halfInput: { width: '48%' },

  payButton: { backgroundColor: '#0e4e96', paddingVertical: 16, borderRadius: 12, alignItems: 'center', marginTop: 8, marginBottom: 16 },
  payButtonText: { color: '#fff', fontSize: 18, fontWeight: '600' },
  securePaymentInfo: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
  securePaymentText: { fontSize: 14, color: '#64748b', marginLeft: 8 },

  processingContainer: { alignItems: 'center', paddingVertical: 60 },
  processingText: { fontSize: 18, color: '#64748b', marginTop: 20 },
  successContainer: { alignItems: 'center', paddingVertical: 60 },
  successTitle: { fontSize: 20, fontWeight: 'bold', color: '#16a34a', marginTop: 20, marginBottom: 12 },
  successText: { fontSize: 16, color: '#64748b', textAlign: 'center', paddingHorizontal: 20 },

  // Locked feature modal styles
  lockedModalContainer: { backgroundColor: '#fff', borderRadius: 16, padding: 30, alignItems: 'center', width: width * 0.85 },
  lockedModalTitle: { fontSize: 20, fontWeight: 'bold', color: '#1e293b', marginTop: 16, marginBottom: 12 },
  lockedModalText: { fontSize: 16, color: '#64748b', textAlign: 'center', lineHeight: 24, marginBottom: 24 },
  lockedModalButtons: { flexDirection: 'row', justifyContent: 'space-between', width: '100%' },
  cancelButton: { flex: 1, paddingVertical: 12, paddingHorizontal: 20, borderRadius: 8, borderWidth: 1, borderColor: '#d1d5db', marginRight: 8, alignItems: 'center' },
  cancelButtonText: { fontSize: 16, color: '#64748b', fontWeight: '500' },
  upgradeButton: { flex: 1, paddingVertical: 12, paddingHorizontal: 20, borderRadius: 8, backgroundColor: '#0e4e96', marginLeft: 8, alignItems: 'center' },
  upgradeButtonText: { fontSize: 16, color: '#fff', fontWeight: '600' },
});

export default WelcomeProfessionalScreen;
